import { useCallback, useEffect, useRef } from "react";
import type { FC } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import {
  add,
  remove,
  set,
  setLoading,
  updateVotes,
  update,
  setIsPausePlaying
} from "../redux/slices/songs";
import { useTypedSelector } from "../redux/store";
import { getAllSongs } from "../utils/getAll";
import { Outlet, useOutletContext } from "react-router-dom";
import { API_URL } from "../api";
import { createSignalRContext } from "react-signalr";

export const PlaylistSRContext = createSignalRContext();

export const PlaylistSRProvider: FC = () => {
  const dispatch = useDispatch();
  const userJwt = useTypedSelector((state) => state.auth.value);

  const isPausePlaying = useTypedSelector(
    (state) => state.songs.isPausePlaying
  );

  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isPausePlaying) {
      timeoutRef.current = setTimeout(() => {
        dispatch(setIsPausePlaying(false));

        toast.info("TV Mode is not active", {
          toastId: "tvMode-not-active"
        });
      }, 10 * 1000);
    }
  }, [isPausePlaying, dispatch]);

  const _getAllSongs = useCallback(() => {
    getAllSongs(
      (res) => {
        dispatch(set(res?.data?.results ?? []));
      },
      () => {
        dispatch(setLoading(false));
      }
    );
  }, [dispatch]);

  PlaylistSRContext.useSignalREffect(
    "PlayNewVideo",
    () => {
      _getAllSongs();
    },
    [_getAllSongs]
  );

  PlaylistSRContext.useSignalREffect(
    "UpdateVideoVotes",
    (message) => {
      dispatch(updateVotes({ id: message.id, votes: message.votes }));
    },
    [dispatch]
  );

  PlaylistSRContext.useSignalREffect(
    "UpdateVideoStatus",
    (song) => {
      dispatch(update(song));

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [dispatch]
  );

  PlaylistSRContext.useSignalREffect(
    "SendNewVideo",
    (message) => {
      dispatch(add({ ...message }));
    },
    [dispatch]
  );

  PlaylistSRContext.useSignalREffect(
    "DeleteVideo",
    (message) => {
      dispatch(remove(message.id));
    },
    [dispatch]
  );

  useEffect(() => {
    _getAllSongs();
  }, [_getAllSongs]);

  return (
    <PlaylistSRContext.Provider
      accessTokenFactory={() => userJwt}
      dependencies={[userJwt]}
      url={`${API_URL}/hubs/playlist`}
      onReconnect={(a) => {
        // TODO: get all songs again
        // TODO: rerender tvmode
        // youtube player does not rerender if same youtube id is passed

        console.log("onReconnect", a);
      }}
      onError={async (a) =>
        new Promise<void>((resolve) => {
          console.log("onError", a);

          if (
            a?.message ===
            "Server timeout elapsed without receiving a message from the server."
          ) {
            toast.error("Connection lost, trying to reconnect...", {
              toastId: "reconnecting"
            });
          }
        })
      }
      onBeforeClose={(a) => {
        console.log("onBeforeClose", a);
      }}
    >
      <Outlet context={useOutletContext()} />
    </PlaylistSRContext.Provider>
  );
};
